/**
 * Formulaire de connexion
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Formulaire de connexion avec email et mot de passe.
 */

 // Client Component pour Next.js

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks';
import { ButtonSpinner } from '../ui/LoadingSpinner';

/**
 * Props du composant LoginForm
 */
export interface LoginFormProps {
  /** Callback après connexion réussie */
  onSuccess?: () => void;
  /** Callback en cas d'erreur */
  onError?: (error: string) => void;
  /** Afficher le lien "Mot de passe oublié" */
  showForgotPassword?: boolean;
  /** Callback du lien "Mot de passe oublié" */
  onForgotPassword?: () => void;
  /** Afficher le lien "Créer un compte" */
  showSignUp?: boolean;
  /** Callback du lien "Créer un compte" */
  onSignUp?: () => void;
  /** Classes CSS additionnelles */
  className?: string;
}

/**
 * Composant LoginForm
 *
 * @example
 * ```tsx
 * function LoginPage() {
 *   const navigate = useNavigate();
 *
 *   return (
 *     <LoginForm
 *       onSuccess={() => navigate('/dashboard')}
 *       onForgotPassword={() => navigate('/reset-password')}
 *       showForgotPassword
 *     />
 *   );
 * }
 * ```
 */
export function LoginForm({
  onSuccess,
  onError,
  showForgotPassword = true,
  onForgotPassword,
  showSignUp = false,
  onSignUp,
  className = '',
}: LoginFormProps) {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    motDePasse: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);

  /**
   * Valider le formulaire
   */
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validation email
    if (!formData.email) {
      errors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Format d\'email invalide';
    }

    // Validation mot de passe
    if (!formData.motDePasse) {
      errors.motDePasse = 'Le mot de passe est requis';
    } else if (formData.motDePasse.length < 8) {
      errors.motDePasse = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Gérer la soumission du formulaire
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setFormErrors({});

    if (!validateForm()) {
      return;
    }

    try {
      await login({
        email: formData.email,
        motDePasse: formData.motDePasse,
      });
      // Pas de redirection ici - elle est gérée par LoginPageClient via useEffect
      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de connexion';
      onError?.(message);
    }
  };

  /**
   * Gérer le changement de champ
   */
  const handleChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    // Effacer l'erreur du champ modifié
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <div className={`w-full max-w-md ${className}`}>
      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        {/* Titre */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-content">
            Connexion
          </h2>
          <p className="mt-2 text-sm text-content-muted">
            Connectez-vous à votre compte
          </p>
        </div>

        {/* Erreur globale */}
        {error && (
          <div
            className="p-4 bg-danger-light border border-danger rounded-lg"
            role="alert"
          >
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-danger mt-0.5 mr-3 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-sm text-danger">{error}</p>
            </div>
          </div>
        )}

        {/* Champ Email */}
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-content-muted mb-1"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            disabled={isLoading}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed ${
              formErrors.email
                ? 'border-danger'
                : 'border-border'
            } bg-surface text-content`}
            placeholder="votre@email.com"
            autoComplete="email"
            aria-invalid={!!formErrors.email}
            aria-describedby={formErrors.email ? 'email-error' : undefined}
          />
          {formErrors.email && (
            <p id="email-error" className="mt-1 text-sm text-danger">
              {formErrors.email}
            </p>
          )}
        </div>

        {/* Champ Mot de passe */}
        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium text-content-muted mb-1"
          >
            Mot de passe
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.motDePasse}
              onChange={handleChange('motDePasse')}
              disabled={isLoading}
              className={`w-full px-4 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed ${
                formErrors.motDePasse
                  ? 'border-danger'
                  : 'border-border'
              } bg-surface text-content`}
              placeholder="••••••••"
              autoComplete="current-password"
              aria-invalid={!!formErrors.motDePasse}
              aria-describedby={formErrors.motDePasse ? 'password-error' : undefined}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content"
              aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              )}
            </button>
          </div>
          {formErrors.motDePasse && (
            <p id="password-error" className="mt-1 text-sm text-danger">
              {formErrors.motDePasse}
            </p>
          )}
        </div>

        {/* Lien mot de passe oublié */}
        {showForgotPassword && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onForgotPassword}
              className="text-sm font-medium text-primary hover:text-primary-hover"
            >
              Mot de passe oublié ?
            </button>
          </div>
        )}

        {/* Bouton de soumission */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-content-inverted bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? (
            <>
              <ButtonSpinner className="mr-2" />
              Connexion en cours...
            </>
          ) : (
            'Se connecter'
          )}
        </button>

        {/* Lien création de compte */}
        {showSignUp && (
          <div className="text-center">
            <p className="text-sm text-content-muted">
              Pas encore de compte ?{' '}
              <button
                type="button"
                onClick={onSignUp}
                className="font-medium text-primary hover:text-primary-hover"
              >
                Créer un compte
              </button>
            </p>
          </div>
        )}
      </form>
    </div>
  );
}
